import { redirect } from "@tanstack/react-router";
import { createMiddleware } from "@tanstack/react-start";
import lodash from "lodash";

import type {
	BOARDS_KEY_MAPPER_ENUM,
	BoardMapper,
} from "@onesolution/constants";
import { useBoardsListing } from "@onesolution/hooks";
import { CACHE_KEYS, cache } from "@onesolution/utils";

import { mondayClient } from "@onesolution/monday/monday";
import type { Context } from ".";
import { authMiddleware } from "./auth";

export type DeepPartial<T> = {
	[K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};

export async function getFormattedBoardsFromCache(
	context: DeepPartial<Context>,
): Promise<BoardMapper> {
	const cacheKey =
		CACHE_KEYS.formattedBoard + context?.user?.sid + context?.user?.email;

	return cache.getOrSet<BoardMapper>(cacheKey, async () => {
		const boardsListing = await mondayClient.query.boardsFromClientWorkspace(
			context as Context,
		);
		const { getBoardsMapper } = useBoardsListing(boardsListing);

		return getBoardsMapper();
	});
}

export const mondayBoards = createMiddleware({ type: "function" })
	.middleware([authMiddleware])
	.server(async ({ next, context }) => {
		const { monday_workspace_id, email, sid } = context.user;

		if (
			lodash.isNil(monday_workspace_id) ||
			lodash.isNil(sid) ||
			lodash.isNil(email)
		) {
			// TODO: add PostHog event here
			throw redirect({ to: "/login" });
		}

		const formattedBoards = await getFormattedBoardsFromCache(context);

		if (lodash.isNil(formattedBoards)) throw new Error("Board list is empty");

		function getBoard(boardName: BOARDS_KEY_MAPPER_ENUM) {
			if (lodash.isNil(formattedBoards)) throw new Error("Board list is empty");

			const boardId = formattedBoards[boardName];

			if (!boardId) throw new Error(`Board ID for "${boardName}" not found`);

			return boardId;
		}

		const boardsProxy = new Proxy(formattedBoards, {
			get(_target, prop) {
				return getBoard(prop as BOARDS_KEY_MAPPER_ENUM);
			},
		});

		return next({
			context: {
				user: context.user,
				boards: boardsProxy,
				getBoard,
			},
		});
	});
